import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { 
  ChevronDown, 
  ArrowLeft, 
  Droplets, 
  Heart, 
  Cross, 
  Church, 
  Users, 
  BookOpen,
  HeartHandshake,
  Download,
  Phone,
  ExternalLink,
  Sparkles,
  Flame,
  UserCheck
} from 'lucide-react';
import { AutoTranslate } from '../hooks/useAutoTranslate';

const StiftelserPage = () => {
  const { t, isRTL } = useLanguage();
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (id) => {
    setOpenSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Introduction text for the page
  const introText = `Kyrkans 7 sakrament är heliga tecken och ord som förmedlar Guds nåd. Detta sker i kraft av Jesu frälsargärning – hans död och uppståndelse. Vi får därför del av hans gärning via dessa sakrament. Själva begreppet "sakrament" kommer från latinets Sacramentum, vars rot är Sacro. Sacro betyder något heligt och avskilt. Begreppet "sakrament" syftar därför rent etymologiskt på en helig handling, verklighet, eller tillstånd.

Av de 7 sakramenten är det flera som kan återupprepas, dessa är bikten, nattvarden och de sjukas smörjelse. Även delvis äktenskapet då någon blir änka eller änkeman (eller då "äktenskapet" visade sig ha varit ogiltigt från början).

Därtill finns det tre sakrament som kallas initiationssakramenten. Dessa initierar oss till det fulla kristna livet och därmed in i Kristi efterföljd. Dessa har därför en samhörighet med varandra och är följande; dopet, nattvarden och konfirmationen. För att ta emot nattvardens sakrament (den heliga Kommunionen) och konfirmationens sakrament måste man gå i trosundervisning, samma sak gäller för äktenskapets sakrament, men det sker i en annan form och gestalt.`;

  const sacraments = [
    // 1. Dopets sakrament
    {
      id: 'baptism',
      icon: Droplets,
      title: 'Dopets sakrament',
      color: 'from-blue-500 to-cyan-600',
      content: {
        intro: 'Dopets sakrament inlemmar oss in i Kyrkan. På sätt och vis är Kyrkan som en kropp, där vi är varandras medlemmar och vars huvud är Kristus. I denna Kyrka har vi också våra helgon, därför är det ett passande bruk att tänka på att ta ett helgonnamn i samband med dopet.',
        items: [
          { name: 'Arvsynden', desc: 'Detta sakrament tar bort det vi kallar för "arv-synden". Arvsynden är det tillstånd vi har hamnat i pga. de första människornas NEJ till Gud.' },
          { name: 'Svaghet till synd', desc: 'Även om arvsynden tas bort i samband med dopet, finns en svaghet till synd kvar i vår mänskliga natur. Arvsynden har gett oss ett sår, även om den i sig har tagits bort i och med dopet.' },
          { name: 'Kristen fostran', desc: 'Böjelsen till synd måste bearbetas bort genom kristen fostran och trosundervisning. Detta blir föräldrarnas och gudmoderns samt gudfaderns roll.' },
          { name: 'Outplånligt insegel', desc: 'Dopet ger ett outplånligt insegel (stämpel) i vår själ som aldrig tas bort. Det ger en Character indelebilis dvs. dopet ger mig min djupaste identitet – att bli Kristus lik – genom min egen personliga karaktär.' },
        ],
        downloads: [
          { name: 'Anmälan om dop (PDF)', url: 'https://customer-assets.emergentagent.com/job_catholic-web/artifacts/rxt0aa5g_01%20-%20DH110%20Anmalan%20om%20dop.pdf' },
          { name: 'Information om dopet (PDF)', url: 'https://www.katolskakyrkan.se/media/1073/dopet-swe-webb.pdf' },
          { name: 'Ordning för barndop (PDF)', url: 'https://www.katolskakyrkan.se/media/1077/barndop.pdf' },
          { name: 'Ordning för vuxendop (PDF)', url: 'https://www.katolskakyrkan.se/media/1078/vuxendopet1.pdf' },
        ]
      }
    },
    // 2. Konfirmationens sakrament
    {
      id: 'confirmation',
      icon: Flame,
      title: 'Konfirmationens sakrament',
      color: 'from-orange-500 to-red-600',
      content: {
        intro: 'Konfirmationens sakrament förverkligar än mer dopets gåva och dess nåd. Det är den Heliga Andes sakrament, vilket gör oss till mogna kristna och som därtill förpliktigar oss till att kunna försvara och förklara tron. När den som döptes vill konfirmeras, bekräftar personen i fråga att hon eller han vill leva efter sitt dop.',
        items: [
          { name: 'Dopets bekräftelse', desc: 'Det brukliga är att dopet skedde när vi var små och vi hade inget ansvar i att säga ja eller nej till dopet. Föräldrarna vill sitt barns bästa därför bestämde de att barnet/barnen skulle döpas.' },
          { name: 'Personligt ställningstagande', desc: 'Nu när jag är myndig och vuxen i min tro vill jag själv bekräfta det som föräldrarna gjorde i mitt ställe när jag döptes som barn.' },
          { name: 'Den Helige Andes gåva', desc: 'Genom konfirmationen tar vi emot den Helige Andes gåvor som styrker oss i vår tro.' },
        ],
        downloads: [
          { name: 'Anmälan om konfirmation (PDF)', url: 'https://customer-assets.emergentagent.com/job_catholic-web/artifacts/tr4xdqf2_01%20-%20DH210%20Anmalan%20om%20konfirmation%20-%20Svenska%20-%20NY.pdf' },
        ]
      }
    },
    // 3. Eukaristins sakrament
    {
      id: 'eucharist',
      icon: Sparkles,
      title: 'Eukaristins sakrament',
      color: 'from-yellow-500 to-amber-600',
      content: {
        intro: 'Eukaristins sakrament är källan och målet (fons et culmen) för det kristna livet. Det är Jesus själv vi tar emot. Han är verkligen närvarande i detta sakrament. Det är inte en symbol för Jesu närvaro, utan är verkligen Jesus själv, närvarande i sakramental form. Därför finns också Tillbedjan av Honom i detta sakrament. Det finns olika namn och begrepp på detta sakrament, var och en av begreppen betonar olika aspekter av Jesu närvaro och effekterna därav.',
        items: [
          { name: 'Eukaristi', desc: '"Eukaristi" kommer från grekiskan och betyder tacksägelse. Vi tackar Gud för att Hans älskade Son kommer till oss och är vår andliga näring. Vi tackar även för Jesu återlösningsverk som vi får del av via detta sakrament.' },
          { name: 'Kommunion', desc: '"Kommunion" kommer från latinet och betyder gemenskap. Här betonar vi vår gemenskap med Jesus och varandra, samt vår gemenskap i tre-enigheten tack vare Jesus och genom detta sakrament.' },
          { name: 'Nattvarden', desc: '"Nattvarden" är ett äldre svenskt uttryck för kvällsmåltid. Vid användning av detta begrepp tänker vi på Jesu sista kvällsmåltid med apostlarna, då Jesus instiftade sakramentet och sade: "Tag och ät härav alla, ty detta är min kropp...".' },
          { name: 'Det allra Heligaste sakramentet', desc: 'Här betonar vi att detta sakrament är målet för de andra sakramenten och för det kristna livet. Det är Jesus själv (i sakramental form) som vi kan ta emot som näring för själen och tillbe.' },
          { name: 'Det Heliga mässoffret', desc: 'Här betonas Jesu korsoffer som aktualiseras och görs närvarande vid varje mässa. Det är samma offer som skedde för ca. 2000 år sedan på korset i Jerusalem, men sker idag på ett oblodigt sätt vid varje eukaristisk gudstjänst.' },
        ]
      }
    },
    // 4. Försoningens sakrament
    {
      id: 'reconciliation',
      icon: HeartHandshake,
      title: 'Försoningens sakrament',
      color: 'from-green-500 to-emerald-600',
      content: {
        intro: 'Försoningens sakrament (bikten). Att bikta regelbundet är att ständigt på nytt och mer medvetet gå in i gemenskapen med Gud och människorna. Ty ingen av oss lever bara för sig själv. Men synden är att välja bara sig själv. Synd är en frivillig handling.',
        items: [
          { name: 'Frivillig handling', desc: 'Vi är fria att välja vår egen tillfredställelse och att blunda för andras behov och vår egen djupaste kallelse till fullkomlighet. Bot är att kämpa mot denna svaghet, med Kristi kors som vapen.' },
          { name: 'Mänsklig värdighet', desc: 'Att bikta kan tyckas vara något förödmjukande, men i själva verket är bikten ett uttryck för vår mänskliga värdighet. För när vi biktar oss tar vi ansvar för våra synder och får upprättelse.' },
          { name: 'Mognad', desc: 'När vi får formulera både vår synd och vårt avståndstagande från synden och vår vilja att hålla fast vid Herren, då mognar vi som människor och som kristna.' },
          { name: 'Vill du bikta dig?', desc: 'Prästen hör bikt före mässorna eller vid överenskommelse.', link: { to: '/contact', text: 'Besök vår kontaktsida för mer information' } },
        ]
      }
    },
    // 5. Äktenskapets sakrament
    {
      id: 'wedding',
      icon: Heart,
      title: 'Äktenskapets sakrament',
      color: 'from-pink-500 to-rose-600',
      content: {
        intro: 'Äktenskapets sakrament är ett livslångt förbund mellan man och kvinna. Målet och meningen med äktenskapet är att makarna ska bli ett ("de är inte längre två, utan ett"), samt att de ska vara öppna för avkomma (nytt mänskligt liv). Äktenskapet kräver att makarna är trogna och sanna mot varandra i nöd och lust, tills döden skiljer dem åt.',
        items: [
          { name: 'Mysterium', desc: 'Den helige Paulus förklarar att äktenskapet mellan döpta kristna innefattar ett mysterium, en hemlighet; nämligen det att deras förhållande speglar och pekar på förhållandet mellan Kristus (brudgummen) och Kyrkan (bruden).' },
          { name: 'Skapelsens ordning', desc: 'Äktenskapet är inte något som Kyrkan har skapat som en institution, utan detta sakrament finns redan inbyggt i skapelsen och som sådan finns det i alla kulturer, språk och religioner.' },
          { name: 'Upphöjt sakrament', desc: 'Det som Kristus gjorde var att han upphöjde äktenskapet i skapelsen och mänsklighetens historia till ett sakrament, dvs en helig handling som ger den nåd makarna behöver för att kunna spegla Guds förbund med människan.' },
        ],
        downloads: [
          { name: '"Vi ska gifta oss" - broschyr (PDF)', url: 'https://www.katolskakyrkan.se/media/1080/gifta-oss-sv-webb.pdf' },
          { name: 'Anmälan om vigsel - Svenska (PDF)', url: 'https://customer-assets.emergentagent.com/job_catholic-web/artifacts/6rvhmw8j_01%20-%20DH410%20Anmalan%20om%20vigsel.pdf' },
          { name: 'Anmälan om vigsel - English (PDF)', url: 'https://customer-assets.emergentagent.com/job_catholic-web/artifacts/zlbtt322_01%20-%20DH411%20Anmalan%20om%20vigsel%20%28utlandet%29.pdf' },
        ]
      }
    },
    // 6. Prästvigningens sakrament
    {
      id: 'ordination',
      icon: UserCheck,
      title: 'Prästvigningens sakrament',
      color: 'from-purple-500 to-indigo-600',
      content: {
        intro: 'Prästvigningen. Jesus kallar även idag män som kan bli hans vittnen och förkunnare. Jesu röst: "Kom och följ mig" kan betyda att någon är kallad till att bli hans apostel – budbärare.',
        items: [
          { name: 'Huvuduppgift', desc: 'En präst har som sin huvuduppgift att förmedla och förvalta flera sakrament och därmed hjälpa sin biskop. Prästen är därmed biskopens närmaste medarbetare i att ge vidare all nåd från Kristi kors och uppståndelse till nutidens människor.' },
          { name: 'Undervisning och mission', desc: 'En annan viktig uppgift för prästen är att undervisa och missionera.' },
          { name: 'Pastor och herde', desc: 'Samt att vara pastor och herde i en lokal församling, dvs att styra och tjäna.' },
        ],
        kallelseLink: '/ovrigt/kallelse'
      }
    },
    // 7. De sjukas smörjelse
    {
      id: 'anointing',
      icon: Cross,
      title: 'De sjukas smörjelse',
      color: 'from-teal-500 to-cyan-600',
      content: {
        intro: 'De sjukas smörjelse. Kyrkan erbjuder smörjelsens sakrament åt alla svårt sjuka för att de, om så är Guds vilja, ska få hälsan igen och för att de ska få syndernas förlåtelse samt lindring och styrka i sitt lidande.',
        items: [
          { name: 'Guds nåd i sjukdom', desc: 'Den som är allvarligt sjuk behöver Guds nåd för att stå emot modlöshet och frestelser och för att bevara själens styrka och förtröstan på Gud.' },
          { name: 'Kan upprepas', desc: 'Sakramentet kan meddelas på nytt om den sjuke tillfrisknar men senare insjuknar på nytt, eller om samma sjukdom leder till en kris.' },
          { name: 'Vid ålderdom', desc: 'Detta sakrament kan även ges till den vars krafter avtagit på grund av allmän ålderdomssvaghet och då bördan, av brist på hälsa, blir för betungande.' },
        ]
      }
    },
  ];

  return (
    <div className="min-h-screen themed-page">
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-80 overflow-hidden bg-gradient-to-r from-purple-700 via-indigo-700 to-purple-800">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <Link to="/fordjupning" className={`inline-flex items-center text-purple-200 hover:text-white mb-4 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}>
              <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              <AutoTranslate>Tillbaka till Fördjupning</AutoTranslate>
            </Link>
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              <AutoTranslate>Sakramenten</AutoTranslate>
            </h1>
            <p className="text-xl text-purple-100 fade-in">
              <AutoTranslate>Kyrkans sju sakrament</AutoTranslate>
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-xl card-hover">
            <CardContent className="p-6 sm:p-8">
              {/* Icon - shown on top for mobile */}
              <div className="flex justify-center md:hidden mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Church className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {/* Icon - hidden on mobile, shown on desktop */}
                <div className="hidden md:flex w-12 h-12 bg-purple-100 rounded-full items-center justify-center flex-shrink-0">
                  <Church className="w-6 h-6 text-purple-600" />
                </div>
                <div className="w-full">
                  <h2 className="text-xl sm:text-2xl font-serif font-bold text-gray-900 mb-4">
                    <AutoTranslate>Allmän introduktion till sakramenten</AutoTranslate>
                  </h2>
                  <div className="text-gray-700 leading-relaxed space-y-4 text-sm sm:text-base">
                    {introText.split('\n\n').map((paragraph, idx) => (
                      <p key={`intro-para-${idx}-${paragraph.slice(0,20)}`}>
                        <AutoTranslate>{paragraph}</AutoTranslate>
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {sacraments.map((section) => {
              const Icon = section.icon;
              const isOpen = openSections[section.id];
              
              return (
                <Card 
                  key={section.id} 
                  className="border-0 shadow-xl card-hover overflow-hidden transition-all duration-300 hover:shadow-xl"
                >
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full text-left"
                    data-testid={`accordion-${section.id}`}
                  >
                    <div className={`flex items-center justify-between p-6 bg-gradient-to-r ${section.color} text-white ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                          <Icon className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-serif font-bold">
                          <AutoTranslate>{section.title}</AutoTranslate>
                        </h3>
                      </div>
                      <ChevronDown 
                        className={`w-6 h-6 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
                      />
                    </div>
                  </button>
                  
                  <div 
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <CardContent className="p-6 bg-white">
                      <p className="text-gray-700 mb-6 leading-relaxed">
                        <AutoTranslate>{section.content.intro}</AutoTranslate>
                      </p>
                      
                      <div className="space-y-3 mb-6">
                        {section.content.items.map((item, idx) => (
                          <div 
                            key={`${section.id}-item-${item.name?.slice(0,15) || idx}`}
                            className={`flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                          >
                            <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${section.color} mt-2 flex-shrink-0`} />
                            <div>
                              <p className="font-semibold text-gray-900">
                                <AutoTranslate>{item.name}</AutoTranslate>
                              </p>
                              <p className="text-sm text-gray-600">
                                <AutoTranslate>{item.desc}</AutoTranslate>
                              </p>
                              {item.link && (
                                <Link
                                  to={item.link.to}
                                  className={`inline-flex text-sm text-blue-600 hover:text-blue-800 mt-1 ${isRTL ? 'justify-end' : ''}`}
                                  data-testid={`section-item-link-${section.id}-${idx}`}
                                >
                                  <AutoTranslate>{item.link.text}</AutoTranslate>
                                </Link>
                              )}
                              {item.isPhone && (
                                <a href="tel:08-6430399" className={`inline-flex items-center text-sm text-blue-600 hover:text-blue-800 mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                  <Phone className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                                  08-643 03 99
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Downloads */}
                      {section.content.downloads && section.content.downloads.length > 0 && (
                        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <h4 className={`font-semibold text-blue-800 mb-3 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Download className="w-4 h-4" />
                            <AutoTranslate>Ladda ner dokument</AutoTranslate>
                          </h4>
                          <div className="space-y-2">
                            {section.content.downloads.map((doc, idx) => (
                              <a 
                                key={`${section.id}-doc-${doc.name?.slice(0,15) || idx}`}
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-2 text-blue-700 hover:text-blue-900 hover:bg-blue-100 p-2 rounded transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                              >
                                <Download className="w-4 h-4 flex-shrink-0" />
                                <span className="text-sm"><AutoTranslate>{doc.name}</AutoTranslate></span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Kallelse link for ordination section */}
                      {section.content.kallelseLink && (
                        <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <h4 className="font-semibold text-purple-800 mb-2">
                            <AutoTranslate>Känner du en kallelse?</AutoTranslate>
                          </h4>
                          <p className="text-sm text-purple-700 mb-3">
                            <AutoTranslate>Om du känner att Gud kallar dig till att bli präst, diakon, nunna eller ordensperson, läs mer om kallelser.</AutoTranslate>
                          </p>
                          <Link 
                            to={section.content.kallelseLink}
                            className={`inline-flex items-center text-sm text-purple-600 hover:text-purple-800 font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
                          >
                            <ExternalLink className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <AutoTranslate>Läs mer om kallelser</AutoTranslate>
                          </Link>
                        </div>
                      )}
                      
                      {section.content.link && (
                        <a 
                          href={section.content.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center text-sm text-purple-600 hover:text-purple-800 font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
                        >
                          <ExternalLink className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <AutoTranslate>Läs mer på katolskakyrkan.se</AutoTranslate>
                        </a>
                      )}
                    </CardContent>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Contact CTA */}
          <Card className="mt-12 border-0 shadow-xl bg-gradient-to-r from-yellow-50 to-amber-50">
            <CardContent className="p-8 text-center">
              <Church className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
              <h3 className="text-2xl font-serif font-bold text-gray-900 mb-3">
                <AutoTranslate>Har du frågor?</AutoTranslate>
              </h3>
              <p className="text-gray-600 mb-6">
                <AutoTranslate>Kontakta vår församling för mer information om sakramenten</AutoTranslate>
              </p>
              <Link 
                to="/contact"
                className="inline-flex items-center px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold rounded-lg transition-colors"
              >
                <AutoTranslate>Kontakta oss</AutoTranslate>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StiftelserPage;
